import { events } from "@constants/tracking";
import { isIntroScreen } from "@features/session/helpers/intro-screens";
import useCurrentSession from "@features/session/hooks/useCurrentSession";
import useSessionStore from "@features/session/hooks/useSessionStore";
import useAnalytics from "@hooks/useAnalytics";
import { useAppStore } from "@hooks/useAppStore";
import { isWeb } from "@memorang/helpers/platform";
import type { Confidence } from "@memorang/types";
import { useDevice } from "@memorang/ui/useDevice";
import { router, useLocalSearchParams } from "expo-router";
import { type RefObject, useCallback, useEffect, useState } from "react";
import type { FlatList } from "react-native";
import type { DialogType } from "../../constants/dialog-configs";
import { hasAnsweredAnyItem } from "../../helpers/session";
import { getGatedAiItemIds } from "../../helpers/summative-ai-gate";
import { runSummativeSubmitGate } from "../../helpers/summative-submit-gate";
import { useAilaStore } from "../../hooks/useAilaStore";
import useEnqueueAssessmentOnLeave from "../../hooks/useEnqueueAssessmentOnLeave";
import { useSession } from "../../hooks/useSession";
import { calculateAndUpdateProgress } from "./helpers";
import { getShowButtonNavFinish } from "./next-button-state";

type FinalizingProgress = { completed: number | null; total: number };

type Props = {
	handleUpdateAnswers: (confidence: Confidence) => void;
	toggleShowEndSessionDialog: () => void;
	setShowSessionDialogData: (data: {
		show: boolean;
		type?: DialogType;
	}) => void;
	showButtonNavSkip: boolean;
	flashlistRef?: RefObject<FlatList | null>;
};

const useSessionNavigationHandlers = ({
	handleUpdateAnswers,
	toggleShowEndSessionDialog,
	setShowSessionDialogData,
	showButtonNavSkip,
	flashlistRef,
}: Props) => {
	const { isMobile } = useDevice();
	const { index } = useLocalSearchParams<{ index: string }>();
	const currentItemIndex = index ? Number.parseInt(index) - 1 : 0;

	const id = useSessionStore((s) => s.id);
	const numericId = useSessionStore((s) => s.numericId);
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const isSummativeTest = useSessionStore((s) => s.isSummativeTest);
	const currentBlockIndex = useSessionStore((s) => s.currentBlockIndex);
	const sessionItems = useSessionStore((s) => s.sessionItems);
	const answers = useSessionStore((s) => s.answers);
	const serverExamBreak = useSessionStore((s) => s.serverExamBreak);
	const breaks = useSessionStore((s) => s.breaks);
	const updateCurrentBlockIndex = useSessionStore(
		(s) => s.updateCurrentBlockIndex,
	);
	const updateProgressForEachBlock = useSessionStore(
		(s) => s.updateProgressForEachBlock,
	);
	const toggleShowBreakDialog = useSessionStore((s) => s.toggleShowBreakDialog);
	const updateFinalizingSession = useSessionStore(
		(s) => s.updateFinalizingSession,
	);

	const updateShowDrawer = useAilaStore((s) => s.updateShowDrawer);
	const { endCurrentSession } = useSession();
	const { explicitCallTrackCustomEvent } = useAnalytics();
	const { currentItem, currentBlock, isLastBlockInPart, numTotalParts } =
		useCurrentSession();

	const testDriver = useAppStore((s) => s.sessionView?.testDriver);
	const enqueueAssessmentOnLeave = useEnqueueAssessmentOnLeave();

	const [finalizingProgress, setFinalizingProgress] =
		useState<FinalizingProgress | null>(null);

	const currentItemId = currentItem?.id!;
	const sectionId = currentItem.metadata?.section;
	const hasAnsweredAtLeastOneItem = hasAnsweredAnyItem(answers);
	const lastBreak = breaks?.length ? breaks[breaks.length - 1] : null;
	const isBreakExpired = lastBreak?.status === "EXPIRED";
	const isLastBlock = isSummativeTest
		? currentBlockIndex === sessionItems.length - 1
		: false;
	const isLastItemInTheBlock = currentItemIndex === currentBlock.length - 1;

	useEffect(() => {
		if (isMobile && !isWeb) {
			flashlistRef?.current?.scrollToIndex({
				index: currentItemIndex,
				animated: true,
			});
		}
	}, [currentItemIndex, flashlistRef, isMobile]);

	const handlePrevious = () => {
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			sessionId: id,
			itemId: currentItemId,
			type: "previous",
		});
		router.setParams({ index: String(currentItemIndex), prev: "true" });
		calculateAndUpdateProgress(useSessionStore.getState(), false);
	};

	const submitSummativeSession = useCallback(
		async (routeParams: { id: number }) => {
			updateFinalizingSession(true);
			const aiItemIds = getGatedAiItemIds(
				sessionItems,
				testDriver,
				isSummativeTest,
			);

			if (aiItemIds.length > 0) {
				setFinalizingProgress({ completed: null, total: aiItemIds.length });
				await runSummativeSubmitGate({
					sessionId: id,
					aiItemIds,
					endSession: () => endCurrentSession({ id }),
					onProgress: (completed, total) =>
						setFinalizingProgress({ completed, total }),
				});
				setFinalizingProgress(null);
			} else {
				endCurrentSession({ id });
			}

			router.replace({
				pathname: "/(protected)/session/[id]/report",
				params: routeParams,
			});
		},
		[
			sessionItems,
			testDriver,
			isSummativeTest,
			id,
			endCurrentSession,
			updateFinalizingSession,
		],
	);

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: nav state machine
	const handleNext = (options?: { skipCompleteTestDialog?: boolean }) => {
		const showButtonNavFinish = getShowButtonNavFinish(
			useSessionStore.getState(),
			currentItemIndex,
		);
		const eventParams = {
			index: currentItemIndex,
			itemId: currentItemId,
			sessionId: id,
			inReviewMode: String(inReviewMode),
		};

		if (!inReviewMode) {
			enqueueAssessmentOnLeave(
				currentItemId,
				isSummativeTest,
				currentItem.metadata?.type,
			);
		}

		if (isIntroScreen(currentItem) && !inReviewMode) {
			if (isLastItemInTheBlock) {
				updateProgressForEachBlock(1, currentBlockIndex);
				updateCurrentBlockIndex(currentBlockIndex + 1);
				router.setParams({ index: "1" });
			} else {
				const newIndex = currentItemIndex + 1;
				router.setParams({ index: String(newIndex + 1), prev: "false" });
			}
			return;
		}

		if (isLastItemInTheBlock && !inReviewMode && isSummativeTest) {
			updateShowDrawer(false);

			const hasParts = numTotalParts > 1;
			const type = isLastBlock
				? "completeTest"
				: hasParts && isLastBlockInPart
					? "completePart"
					: "completeSection";
			if (isSummativeTest && !inReviewMode) {
				handleUpdateAnswers(50);
			}
			calculateAndUpdateProgress(useSessionStore.getState(), true);
			const breakMatchesSection =
				serverExamBreak &&
				serverExamBreak.section === sectionId &&
				serverExamBreak.status !== "EXPIRED" &&
				!isBreakExpired;
			if (breakMatchesSection) {
				toggleShowBreakDialog(true);
			} else if (options?.skipCompleteTestDialog && isLastBlock) {
				submitSummativeSession({ id: numericId });
			} else {
				setShowSessionDialogData({ show: true, type });
			}
			return;
		}

		if (showButtonNavFinish || isLastItemInTheBlock) {
			updateShowDrawer(false);
			explicitCallTrackCustomEvent({
				eventName: events.sessionFinishNavClicked,
				...eventParams,
			});
			if (inReviewMode) {
				router.back();
			} else if (hasAnsweredAtLeastOneItem) {
				explicitCallTrackCustomEvent({
					eventName: events.sessionItemNavigated,
					...eventParams,
					type: "finish",
				});
				submitSummativeSession({ id });
			} else {
				toggleShowEndSessionDialog();
			}
			return;
		}

		const newIndex = currentItemIndex + 1;
		if (inReviewMode) {
			updateShowDrawer(false);
		}
		if ((isSummativeTest || showButtonNavSkip) && !inReviewMode) {
			handleUpdateAnswers(50);
		}
		calculateAndUpdateProgress(useSessionStore.getState(), true);
		explicitCallTrackCustomEvent({
			eventName: events.sessionItemNavigated,
			...eventParams,
			type: "next",
		});
		router.setParams({ index: String(newIndex + 1), prev: "false" });
	};

	const moveToNextBlock = useCallback(() => {
		if (isLastBlock) {
			submitSummativeSession({ id: numericId });
		} else {
			router.setParams({ index: "1", type: "next" });
			updateCurrentBlockIndex(currentBlockIndex + 1);
		}
	}, [
		isLastBlock,
		submitSummativeSession,
		numericId,
		updateCurrentBlockIndex,
		currentBlockIndex,
	]);

	return {
		handleNext,
		handlePrevious,
		moveToNextBlock,
		finalizingProgress,
	};
};

export default useSessionNavigationHandlers;
